import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useEffect, useCallback } from 'react';
import { IconCheckboxCheckSolid, IconCheckboxIndeterminateSolid } from '@circlefin/euclid-icons/common';
import classNames from 'classnames';
import { CheckboxGroup } from './Checkbox.Group/Checkbox.Group.es.js';
import { useCheckboxGroupContext } from './Checkbox.Group.Context/index.es.js';

const Checkbox = /*#__PURE__*/ forwardRef(({ checked = false, label, helpText, error, onChange, className, indeterminate = false, disabled, value, ...rest }, refProp)=>{
    const defaultRef = useRef(null);
    const resolvedRef = refProp ?? defaultRef;
    const helpTextId = useId();
    // If CheckboxGroup Context.Provider is present, derive props based on the
    // Provider wrapper props; otherwise, return original props as-is.
    const context = useCheckboxGroupContext({
        checked,
        disabled,
        onChange,
        value
    });
    /**
     * The `indeterminate` state of the checkbox is managed via JavaScript using a `ref` set in `useEffect`.
     *
     * The `Checkbox` supports an optional `ref` via `forwardRef`.
     * If none is provided, a default `ref` is used.
     *
     * `resolvedRef` represents the default or user-provided `ref`.
     */ useEffect(()=>{
        if (typeof resolvedRef !== 'function' && resolvedRef.current) {
            resolvedRef.current.indeterminate = indeterminate;
        }
    }, [
        resolvedRef,
        indeterminate
    ]);
    const CheckedIcon = useCallback(()=>{
        if (context.checked) {
            return jsx(IconCheckboxCheckSolid, {
                size: 16
            });
        }
        if (indeterminate) {
            return jsx(IconCheckboxIndeterminateSolid, {
                size: 16
            });
        }
        return null;
    }, [
        context.checked,
        indeterminate
    ]);
    return jsxs("div", {
        children: [
            jsxs("label", {
                className: classNames('cb-checkbox', {
                    error,
                    disabled: context.disabled
                }, className),
                "data-testid": "checkbox",
                children: [
                    jsx("input", {
                        ...rest,
                        ref: resolvedRef,
                        "aria-describedby": helpText != null ? helpTextId : '',
                        checked: context.checked,
                        className: "checkbox-input",
                        disabled: context.disabled,
                        onChange: context.onChange,
                        type: "checkbox",
                        value: value
                    }),
                    jsx("span", {
                        className: "checkbox",
                        children: jsx(CheckedIcon, {})
                    }),
                    label != null && jsx("span", {
                        className: "label",
                        children: label
                    })
                ]
            }),
            helpText != null && jsx("div", {
                className: "help-text",
                "data-testid": "help-text",
                id: helpTextId,
                children: helpText
            })
        ]
    });
});
Checkbox.displayName = 'Checkbox';
const CheckboxNamespace = Checkbox;
CheckboxNamespace.Group = CheckboxGroup;

export { CheckboxNamespace as Checkbox, CheckboxNamespace as default };
//# sourceMappingURL=Checkbox.es.js.map
